import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/ui/form-field";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useCreateOrganization } from "@/hooks/queries/useCreateOrganization";
import {
  CREATE_ORGANIZATION_ORG_TYPE,
  createOrganizationSchema,
  type CreateOrganizationValues,
} from "@/routes/admin/create-organization-schema";
import {
  toCreateOrganizationError,
  type CreateOrganizationBanner,
  type CreateOrganizationFieldErrors,
} from "@/routes/admin/create-organization-error";

// Story 15.1 — the platform provisioning form (Screen 33).
//
// ELEVEN fields in the mockup's own three sections and field order: the nine FR74
// commercial fields MINUS `org_type`, plus the first org_admin's email and the two
// name fields Screen 33 does not draw. The deltas are recorded in the story's W3
// field contract and are the expected post-dev mockup diff.
//
// ORG TYPE IS NOT A CONTROL. The mockup renders a `<select>` holding the single
// option "Professional"; this screen renders the value as read-only text. The
// request schema does not accept the field at all — this endpoint creates
// professional organizations only, and personal ones are auto-created alongside
// their owner — so a select would offer a choice that has nowhere to go.
//
// THE FIRST ORGANIZATION ADMIN IS REQUIRED, and the mockup's two "invite them
// later" surfaces are rewritten to say so. The organization's `owner_user_id` is
// NOT NULL from the first instant the row exists (FR73) and both are created in one
// transaction, so there is no skip path for the copy to promise.
//
// NO TEMPORARY PASSWORD TOUCHES THIS SCREEN. The endpoint generates one, mails it
// from a post-commit background task, and returns a 201 that carries no password,
// no hash and no email address by contract.

/** The `<input type="date">` values are already `YYYY-MM-DD`; nothing to convert. */
type FieldName = keyof CreateOrganizationFieldErrors;

const FIELD_NAMES: readonly FieldName[] = [
  "name",
  "externalReferenceId",
  "billingAddress",
  "seatCapacity",
  "contractStartDate",
  "contractEndDate",
  "billingContactEmail",
  "securityContactEmail",
  "adminEmail",
  "adminFirstName",
  "adminLastName",
];

export function CreateOrganizationScreen() {
  const navigate = useNavigate();
  const [banner, setBanner] = React.useState<CreateOrganizationBanner | null>(null);

  const createOrganization = useCreateOrganization();

  const form = useForm<CreateOrganizationValues>({
    resolver: zodResolver(createOrganizationSchema),
    defaultValues: {
      name: "",
      externalReferenceId: "",
      billingAddress: "",
      // The mockup's `value="40"` is a sample, not a default: seat capacity is
      // required with NO default (AC8), so an operator who never touches this field
      // must be refused rather than quietly sold forty seats.
      seatCapacity: "",
      contractStartDate: "",
      contractEndDate: "",
      billingContactEmail: "",
      securityContactEmail: "",
      adminEmail: "",
      adminFirstName: "",
      adminLastName: "",
    },
  });

  const onSubmit = form.handleSubmit((values) => {
    if (createOrganization.isPending) return;
    setBanner(null);
    createOrganization.mutate(
      {
        name: values.name.trim(),
        // Story 15.6 AC11/AC12 — a blank CRM key is sent as `null`, never as
        // `""`: the column is UNIQUE, and it admits many NULLs but exactly one
        // empty string, so the second organization provisioned without a key
        // would collect a 409 ORG-016 against a value nobody typed. The endpoint
        // normalises a blank too; this keeps the wire honest either way.
        external_reference_id: values.externalReferenceId.trim() || null,
        billing_address: values.billingAddress.trim(),
        seat_capacity: Number(values.seatCapacity),
        contract_start_date: values.contractStartDate,
        contract_end_date: values.contractEndDate,
        billing_contact_email: values.billingContactEmail.trim(),
        security_contact_email: values.securityContactEmail.trim(),
        admin_email: values.adminEmail.trim(),
        admin_first_name: values.adminFirstName.trim(),
        admin_last_name: values.adminLastName.trim(),
      },
      {
        onSuccess: (data) => {
          // The organization exists and the operator has nothing left to do here;
          // the detail route is the record of what they created (AC16). Registered
          // in this story with a placeholder leaf, so this is never a dead link.
          navigate(`/admin/orgs/${data.organization_id}`);
        },
        onError: (error) => {
          const mapped = toCreateOrganizationError(error);
          // Refusals are INLINE wherever a field can carry them: eleven fields is a
          // lot to retype because one CRM key collided, so nothing here clears the
          // form and nothing navigates away.
          for (const field of FIELD_NAMES) {
            const message = mapped.fieldErrors[field];
            if (message) form.setError(field, { message });
          }
          setBanner(mapped.banner);
        },
      },
    );
  });

  return (
    <section className="mx-auto flex w-full max-w-[var(--width-xl)] flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="display-xs text-[color:var(--text-primary)]">Add new organization</h1>
        {/* The mockup's sub-line (screen 33, content-header), first sentence kept
            verbatim. Its second — "This is separate from opening an existing
            organization in the directory." — is replaced rather than dropped: it
            points at a directory this story does not ship (seam-151), and the
            sentence that earns the space instead is the one the operator cannot
            infer, that the first administrator is created WITH the organization in
            the same transaction. (The mockup's "organization admins are invited
            separately" clause is a different surface — the SIDEBAR note, which the
            app shell does not render — and it is contradicted for the same reason.) */}
        <p className="text-sm text-[color:var(--text-tertiary)]">
          Register a new organization, set its first seat block, and attach commercial
          metadata. The first organization administrator is created together with the
          organization and cannot be added later from this screen.
        </p>
      </header>

      <Banner variant="info" title="Entitlements">
        Invites and seat assignments cannot exceed the entitled count you set here; the
        platform enforces the cap after the tenant is created.
      </Banner>

      {banner ? (
        <Banner variant={banner.variant} title={banner.title}>
          <div className="flex flex-col gap-2">
            <span>{banner.body}</span>
            {banner.code ? <span>{`Reference code: ${banner.code}`}</span> : null}
          </div>
        </Banner>
      ) : null}

      <form className="flex flex-col gap-8" onSubmit={onSubmit} noValidate>
        <fieldset className="flex flex-col gap-4">
          <legend className="text-base font-semibold text-[color:var(--text-primary)]">
            Organization profile
          </legend>
          <div className="grid gap-4 sm:grid-cols-3">
            <FormField
              label="Legal organization name"
              error={form.formState.errors.name?.message}
              required
            >
              <Input
                autoComplete="off"
                placeholder="e.g. Northern Metropolitan NHS Trust"
                {...form.register("name")}
              />
            </FormField>
            {/* Story 15.6 AC11 — NOT `required`: CITS provisions organizations
                ahead of the CRM record existing, so the key is settable once
                afterwards on the detail page's Contract card. */}
            <FormField
              label="External reference ID"
              error={form.formState.errors.externalReferenceId?.message}
              helper="Optional. Can be set once later, from the organization's Contract card."
            >
              <Input
                autoComplete="off"
                placeholder="CRM key supplied by CITS"
                {...form.register("externalReferenceId")}
              />
            </FormField>
            {/* Read-only text, NOT a disabled select: a control the operator cannot
                use still invites them to look for the other options. */}
            <div className="grid content-start gap-[6px]">
              <span className="text-sm font-medium text-[color:var(--text-secondary)]">
                Org type
              </span>
              <p
                className="text-sm capitalize text-[color:var(--text-primary)]"
                data-testid="org-type-value"
              >
                {CREATE_ORGANIZATION_ORG_TYPE}
              </p>
              <p className="text-sm text-[color:var(--text-tertiary)]">
                This screen creates professional organizations only.
              </p>
            </div>
            <FormField
              className="sm:col-span-3"
              label="Billing address"
              error={form.formState.errors.billingAddress?.message}
              required
            >
              <Textarea
                autoComplete="off"
                placeholder="Street, city, postal code, country"
                {...form.register("billingAddress")}
              />
            </FormField>
          </div>
        </fieldset>

        <fieldset className="flex flex-col gap-4">
          <legend className="text-base font-semibold text-[color:var(--text-primary)]">
            Seat capacity &amp; contract dates
          </legend>
          <p className="text-sm text-[color:var(--text-tertiary)]">
            Initial seat capacity for this tenant. Capacity can be adjusted up or down
            later from the organization detail. Contract is defined by the start/end
            date pair — no proration, no rollover, no enumerated term length.
          </p>
          <div className="grid gap-4 sm:grid-cols-3">
            <FormField
              label="Seat capacity"
              error={form.formState.errors.seatCapacity?.message}
              required
            >
              <Input
                type="number"
                min={1}
                step={1}
                inputMode="numeric"
                {...form.register("seatCapacity")}
              />
            </FormField>
            <FormField
              label="Contract start date"
              error={form.formState.errors.contractStartDate?.message}
              // The one field on this form whose absence is unrecoverable in-product:
              // the contract guard fails closed on a NULL date and no endpoint sets a
              // START date afterwards, so an organization created without one is
              // permanently write-locked (DW-67).
              helper="Required — a contract with no start date cannot be extended later."
              required
            >
              <Input type="date" {...form.register("contractStartDate")} />
            </FormField>
            <FormField
              label="Contract end date"
              error={form.formState.errors.contractEndDate?.message}
              required
            >
              <Input type="date" {...form.register("contractEndDate")} />
            </FormField>
          </div>
        </fieldset>

        <fieldset className="flex flex-col gap-4">
          <legend className="text-base font-semibold text-[color:var(--text-primary)]">
            Primary contacts
          </legend>
          <div className="grid gap-4 sm:grid-cols-3">
            <FormField
              label="Billing contact email"
              error={form.formState.errors.billingContactEmail?.message}
              required
            >
              <Input
                type="email"
                autoComplete="off"
                placeholder="procurement@example.org"
                {...form.register("billingContactEmail")}
              />
            </FormField>
            <FormField
              label="Security contact email"
              error={form.formState.errors.securityContactEmail?.message}
              required
            >
              <Input
                type="email"
                autoComplete="off"
                placeholder="security@example.org"
                {...form.register("securityContactEmail")}
              />
            </FormField>
            {/* The mockup's `(optional)` marker is removed — this is the owner. */}
            <FormField
              label="First organization admin"
              error={form.formState.errors.adminEmail?.message}
              required
            >
              <Input
                type="email"
                autoComplete="off"
                placeholder="clinical.director@example.org"
                {...form.register("adminEmail")}
              />
            </FormField>
            {/* The +2 delta: Screen 33 draws neither, and without them the account
                renders nameless in the Admin Console list and in 15.6's member
                deep-link. */}
            <FormField
              label="First name"
              error={form.formState.errors.adminFirstName?.message}
              required
            >
              <Input
                autoComplete="off"
                placeholder="Given name"
                {...form.register("adminFirstName")}
              />
            </FormField>
            <FormField
              label="Last name"
              error={form.formState.errors.adminLastName?.message}
              required
            >
              <Input
                autoComplete="off"
                placeholder="Family name"
                {...form.register("adminLastName")}
              />
            </FormField>
          </div>
          {/* Replaces the mockup's "you can skip and invite the first org admin
              later" helper. Under variant A there is no such path. */}
          <p className="text-sm text-[color:var(--text-tertiary)]">
            This administrator owns the organization and is created with it. They are
            emailed a temporary password and are asked to change it on their first
            sign-in — the password is never shown here.
          </p>
        </fieldset>

        <Banner variant="info" title="Billing is handled outside the platform">
          CITS invoices and payments are not stored here. This screen captures
          organization data only — seat capacity, contract dates, and operational
          contacts.
        </Banner>

        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" variant="primary" disabled={createOrganization.isPending}>
            {createOrganization.isPending ? "Creating…" : "Add organization"}
          </Button>
        </div>
      </form>
    </section>
  );
}

export default CreateOrganizationScreen;
